import { Stack, Typography } from "@mui/material";
import { pdf, PDFViewer } from "@react-pdf/renderer";
import { createContext, useEffect, useMemo, useState } from "react";
import ErrorBoundary from "../common/components/ErrorBoundary";
import Loader from "../common/components/fundamentals/Loader/Loader";
import Doc from "./Doc";
import { cvTranslations } from "./translations";

export const CVContentContext = createContext(null);
export const LanguageContext = createContext<"en" | "de">("en");

function PdfRenderErrorMessage({ error }: { error: string | null }) {
  return (
    <Stack
      sx={{
        height: "100%",
        alignItems: "center",
        justifyContent: "center",
        padding: "2rem",
        gap: "0.75rem",
        textAlign: "center",
      }}
    >
      <Typography variant="h6" sx={{ color: "error.main" }}>
        PDF preview unavailable
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: "32rem" }}>
        The CV preview could not be rendered for this candidate. Your edits on
        the right are still saved. Try simplifying the CV JSON (remove one
        project, requirement, or large block at a time) until the preview
        returns, then save again.
      </Typography>
      {error && (
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", fontFamily: "monospace", marginTop: "0.5rem" }}
        >
          {error}
        </Typography>
      )}
    </Stack>
  );
}

export default function CVPDF({
  cvContent,
  setPdfDownloadBlob,
  isGenerating = false,
  isGeneratingCV = false,
  language = "en",
  displayedMatchScore,
}: {
  cvContent: any | null;
  setPdfDownloadBlob: (blob: Blob | null) => void;
  isGenerating?: boolean;
  isGeneratingCV?: boolean;
  language?: "en" | "de";
  displayedMatchScore?: number;
}) {
  const [document, setDocument] = useState<JSX.Element | null>(null);
  const [renderError, setRenderError] = useState<string | null>(null);

  // Validate CV content
  const isValidCV = useMemo(() => {
    return !!(
      cvContent &&
      cvContent.jobTitle &&
      cvContent.fullName &&
      cvContent.headlines &&
      cvContent.languages &&
      cvContent.location &&
      cvContent.nationality &&
      cvContent.professionalCareer &&
      cvContent.projects
    );
  }, [cvContent]);

  useEffect(() => {
    let cancelled = false;
    const getRenderBlob = async () => {
      if (isValidCV && cvContent) {
        const doc = <Doc cvContent={cvContent} language={language} />;
        try {
          // Probe Yoga/react-pdf with toBlob FIRST. If layout fails (e.g. Yoga
          // throws "Invalid array length" on pathological content), we catch
          // the rejection here and never mount <PDFViewer>, which would hit
          // the same engine and hang the page silently.
          const blob = await pdf(doc).toBlob();
          if (cancelled) return;
          setDocument(doc);
          setPdfDownloadBlob(blob);
          setRenderError(null);
        } catch (e) {
          if (cancelled) return;
          // eslint-disable-next-line no-console
          console.error("PDF render failed", e);
          setDocument(null);
          setPdfDownloadBlob(null);
          setRenderError(e instanceof Error ? e.message : String(e));
        }
      } else if (!isValidCV && !cvContent) {
        // Only clear if we truly have no content (e.g., when switching candidates)
        setDocument(null);
        setPdfDownloadBlob(null);
        setRenderError(null);
      }
      // If isValidCV is false but we have cvContent, keep the current document
    };

    getRenderBlob();
    return () => {
      cancelled = true;
    };
  }, [cvContent, setPdfDownloadBlob, isValidCV, language]);

  const showPdf = isValidCV && document && !isGenerating && !isGeneratingCV;

  return (
    <Stack
      sx={{
        flexDirection: "column",
        width: "100%",
        minHeight: "100%",
        height: "100%",
      }}
    >
      {showPdf ? (
        // Even though we probed with toBlob() first, wrap in ErrorBoundary
        // as a second-line defence in case PDFViewer throws synchronously on
        // a later re-render.
        <ErrorBoundary
          fallback={() => <PdfRenderErrorMessage error={renderError} />}
        >
          <PDFViewer
            style={{
              height: "100%",
            }}
            showToolbar={false}
          >
            {document!}
          </PDFViewer>
        </ErrorBoundary>
      ) : renderError ? (
        <PdfRenderErrorMessage error={renderError} />
      ) : (
        <Stack
          sx={{
            height: "100%",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isGenerating || isGeneratingCV || cvContent === null || (isValidCV && !document) ? (
            <Loader />
          ) : (
            <Typography>
              {displayedMatchScore !== undefined &&
                displayedMatchScore < 6 &&
                "Match score below 6, "}
              {cvTranslations[language].cvNotAvailable}
            </Typography>
          )}
        </Stack>
      )}
    </Stack>
  );
}
